import { getYear, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import { OrderType } from '../../domain/schemas';
import { DataUnitDataListTransitionResponse } from '../../domain/unitDataList/types';

// Unicode定数を定義
const ZERO_WIDTH_SPACE_REGEX = /\u200B/g;
const IDEOGRAPHIC_SPACE_REGEX = /\u3000/g;
const FULLWIDTH_SLASH_REGEX = /／/g;
const FULLWIDTH_DIGITS_REGEX = /[０-９]/g;
const FULLWIDTH_TO_HALFWIDTH_OFFSET = 0xfee0;

/**
 * 日付文字列を正規化する
 * - ゼロ幅スペース・全角スペースを削除
 * - 全角スラッシュを半角スラッシュに変換
 * - 全角数字を半角数字に変換
 */
const normalizeDateString = (raw: string): string => {
  return raw
    .replace(ZERO_WIDTH_SPACE_REGEX, '') // ゼロ幅スペース削除
    .trim() // 前後の空白削除
    .replace(IDEOGRAPHIC_SPACE_REGEX, '') // 全角スペース削除
    .replace(FULLWIDTH_SLASH_REGEX, '/') // 全角スラッシュ → 半角スラッシュ
    .replace(
      FULLWIDTH_DIGITS_REGEX,
      (char) =>
        String.fromCharCode(char.charCodeAt(0) - FULLWIDTH_TO_HALFWIDTH_OFFSET) // 全角数字 → 半角数字
    );
};

/**
 * MM/DD 形式の日付に「年」を補完し、start〜end の期間内に入る Date を生成する
 *
 * @param month - 月（0始まり、0=1月、11=12月）
 * @param day - 日
 * @param start - 期間の開始日
 * @param end - 期間の終了日
 * @returns 期間内に入る最初の年で生成したDateオブジェクト。見つからない場合はnull
 *
 * 【現在の前提仕様】
 * - 本テーブルのデータ期間は最大でも1年未満（例：過去28日）
 * - そのため同じ MM/DD が期間内に複数回出現しない想定
 *
 * 【実装ロジック】
 * - startYear から endYear まで順に探索し、
 *   期間内に入る「最初の年」を採用する
 *
 * 【⚠ 重要な制約（将来リスク）】
 * - データ期間が1年以上になると同じ MM/DD が複数年に存在するため、
 *   どの年に属するか一意に決定できない
 * - その場合、この実装では全て最初の年として扱われ誤ったソート結果になる
 *
 * 【将来の改修方針案】
 * - バックエンドから yyyy-MM-dd 形式（年付き）で返却する
 * - もしくは年を判定する追加ロジックを導入する
 */
const findDateInYearRange = (
  month: number,
  day: number,
  start: Date,
  end: Date
): Date | null => {
  const startYear = getYear(start);
  const endYear = getYear(end);

  // 範囲内の年を優先して探す
  for (let y = startYear; y <= endYear; y++) {
    const candidateDate = new Date(y, month, day);
    // JSの仕様で「2/31」が「3/3」に変換されるのを防ぐため、月の不一致をチェック
    if (
      candidateDate.getMonth() === month &&
      candidateDate.getTime() >= start.getTime() &&
      candidateDate.getTime() <= end.getTime()
    ) {
      return candidateDate;
    }
  }

  return null;
};

/**
 * テーブルの行データを「日付」でソートする関数。
 *
 * 【対応フォーマット】
 * - MM/DD（年なし → start/end の範囲から推測）
 *
 * 【仕様】
 * - パースできない値（'-' や空文字）は末尾へ送る
 * - 平均/合計行はソート結果に関係なく常に先頭に配置する
 */
export const sortDate = (
  rows: DataUnitDataListTransitionResponse['data']['rows'],
  order: OrderType,
  startDate: string,
  endDate: string
): DataUnitDataListTransitionResponse['data']['rows'] => {
  /**
   * 文字列の日付を Date に変換する簡易パーサ
   *
   * - 不正値や '-' は null を返す
   * - 全角数字/スラッシュなどを正規化してから解析する
   * - 年がない (MM/DD) 形式は startDate/endDate の範囲から年を推測する
   */
  const parseToDate = (
    raw: string,
    sDate: string,
    eDate: string
  ): Date | null => {
    // 「平均/合計」などの特殊文字列や空文字を除外
    if (!raw || raw === '-' || raw === '平均/合計') return null;

    // 正規化処理
    const normalizedDate = normalizeDateString(raw);

    /**
     * MM/DD 形式（年なし）
     * startDate〜endDate の期間内に入る年を探索して補完する
     */
    const monthDayMatch = normalizedDate.match(/^(\d{1,2})\/(\d{1,2})$/);
    if (monthDayMatch) {
      const month = parseInt(monthDayMatch[1], 10) - 1;
      const day = parseInt(monthDayMatch[2], 10);

      // date-fnsのparseISOを使用して日付を安全にパース
      // Asia/Tokyoタイムゾーンに変換して日付の解釈を統一
      const start = utcToZonedTime(parseISO(sDate), 'Asia/Tokyo');
      const end = utcToZonedTime(parseISO(eDate), 'Asia/Tokyo');

      return findDateInYearRange(month, day, start, end);
    }

    return null;
  };

  /**
   * ソート前に timestamp を計算してキャッシュ
   * （sort 中の再パースを防ぎパフォーマンス向上）
   * 「平均/合計」行を常に先頭に配置するため、このタイミングで分離します
   */
  const summaryRows: DataUnitDataListTransitionResponse['data']['rows'] = [];
  const normalRowsWithKey: {
    idx: number;
    key: number | null;
    row: (typeof rows)[0];
  }[] = [];

  /**
   * 各行の日付をパースしてtimestampに変換
   *
   * 【注意】現在の仕様（過去28日のみ）では問題ありませんが、
   * 将来データが一年以上になった場合、同じMM/DD形式の日付が複数存在すると
   * parseToDateが最初に見つかった年を返すため、全て最初の年で扱われます。
   * その場合は要件改修が必要です（例：バックエンドから年を含めた形式で返す、比較式を追加する等）
   */
  rows.forEach((row, idx) => {
    if (row.date === '平均/合計' && row.day === '-') {
      summaryRows.push(row);
    } else {
      const t = parseToDate(row.date, startDate, endDate);
      normalRowsWithKey.push({ idx, key: t ? t.getTime() : null, row: row });
    }
  });

  /**
   * 日付キーでソート
   * - null は最後尾
   * - 同値は元の順序を維持（stable sort）
   */
  normalRowsWithKey.sort((a, b) => {
    const keyA = a.key;
    const keyB = b.key;

    // 両方 null の場合は元の位置を維持
    if (keyA == null && keyB == null) return a.idx - b.idx;
    // 片方が null の場合は最後尾へ
    if (keyA == null) return 1;
    if (keyB == null) return -1;

    return order === 'asc' ? keyA - keyB : keyB - keyA;
  });

  /**
   * 「平均/合計」行は常に先頭固定
   */
  const sortedNormalRows = normalRowsWithKey.map((x) => x.row);

  return [...summaryRows, ...sortedNormalRows];
};
